import axios, { AxiosResponse } from "axios";
import { config } from "../../contexts/UrlContext";
import { toast } from "react-toastify";
import React, { useEffect, useState, useContext } from 'react';
import { ChromePicker } from 'react-color';
import { Button, Dimmer, Grid, Loader , Segment, Dropdown, GridRow} from 'semantic-ui-react';
import { ITheme } from "../Interfaces/TableInterface";
import { SettingsContext } from "../../contexts/SettingsContext";
import ErrorPage from "../ErrorPage/ErrorPage";
import AuthContext from "../../contexts/AuthContext";
import { postRequest } from "../../services/axiosService";


export const borderOptions = [
    { key: 0, text: 'Solid', value: "Solid" },
    { key: 1, text: 'Dotted', value: "Dotted" },
    { key: 2, text: 'Dashed', value: "Dashed" },
    { key: 3, text: 'Double', value: "Double" },
    { key: 4, text: 'Hidden', value: "Hidden" },
    { key: 5, text: 'Ridge', value: "Ridge" }
]

export const fontOptions = [
    { key: 0, text: 'Arial', value: "Arial" },
    { key: 1, text: 'Courier', value: "Courier" },
    { key: 2, text: 'Helvetica', value: "Helvetica" },
    { key: 3, text: 'Segoe UI', value: "Segoe UI" },
    { key: 4, text: 'Times New Roman', value: "Times New Roman" },
    { key: 5, text: 'Verdana', value: "Verdana" }
]

function ThemeConfig() {
    let search = window.location.search;
    let params = new URLSearchParams(search);
    let themeId: string = params.get('id')!;

    const { auth } = useContext(AuthContext);


    const { hasAdminPrivileges } = useContext(SettingsContext);

    const [name, setName] = useState<string>("");
    const [headerColour, setHeaderColour] = useState<string>("#FFFFFF");
    const [headerFontColour, setHeaderFontColour] = useState<string>("#FFFFFF");
    const [tableBackgroundColour, setTableBackgroundColour] = useState<string>("#FFFFFF");
    const [tableFontColour, setTableFontColour] = useState<string>("#FFFFFF");
    const [fontSize, setFontSize] = useState<string>("14");
    const [fontFamily, setFontFamily] = useState<string>("Segoe UI");
    const [tableBorder, setTableBorder] = useState<string>("");

    const [headerFontDisplay, setHeaderFontDislay] = useState<boolean>(false);
    const [headerTableDisplay, setHeaderTableDisplay] = useState<boolean>(false);
    const [primaryFontDisplay, setPrimaryFontDisplay] = useState<boolean>(false);
    const [backgroundDisplay, setBackgroundDisplay] = useState<boolean>(false);

    const [saveDisabled, setSavedDisabled] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        async function getData() {
            if (themeId !== null) {
                await getThemeSettings();
            }
            setLoading(false);
        }
        getData();
    }, [])

    const getThemeSettings = async () => {
        try {
            let response: AxiosResponse;

            if (!auth || auth === null) {
                response = await axios.get(`${config.baseUrl}/api/Theme/GetTheme?id=${themeId}`);
            } else {
                let header = {
                    Authorization: `Bearer ${auth.user?.access_token}`,
                }
                response = await axios.get(`${config.baseUrl}/api/Theme/GetTheme?id=${themeId}`, { headers: header });
            }

            if (response) {
                let data = response.data;
                setName (data.name);
                setHeaderColour(data.headerColour);
                setHeaderFontColour(data.headerFontColour);
                setTableBackgroundColour(data.tableBackgroundColour);
                setTableFontColour(data.tableFontColour);
                setFontSize(data.fontSize);
                setFontFamily(data.fontFamily);
                setTableBorder(data.tableBorder);  
            }
        } catch (error) {
            console.log(error);
            toast.error(`Unable to get theme. See logs for more info. `);
        }
    }

    const handleBlur = (e: any) => {
        if (e.currentTarget.value === '0') e.currentTarget.value = '1'
    }
    const handleKeypress = (e: any) => {
        const characterCode = e.key
        if (characterCode === 'Backspace') return

        const characterNumber = Number(characterCode)
        if (characterNumber >= 0 && characterNumber <= 9) {
            if (e.currentTarget.value && e.currentTarget.value.length) {
                return
            } else if (characterNumber === 0) {
                e.preventDefault()
            }
        } else {
            e.preventDefault()
        }
    }
    const onFontChange = (e: any, data: any) => {
        setFontFamily(data.value);
    }

    const onTableBorderChange = (e: any, data: any) => {
        setTableBorder(data.value);
    }

    const saveTheme = async () => {
        setSavedDisabled(true);

        if (name === "" || name === undefined) {
            toast.error(`Theme name cannot be empty`);
            setSavedDisabled(false);
            return;
        }

        if (tableBorder === null || tableBorder === undefined) {
            toast.error(`Table border design cannot be empty`);
            setSavedDisabled(false);
            return;
        }

        if (fontSize === null || fontSize === undefined) {
            toast.error("Font size cannot be empty");
            setSavedDisabled(false);
            return;
        }

        if (fontFamily === null || fontFamily === undefined) {
            toast.error("Font cannot be left blank");
            setSavedDisabled(false);
            return;
        }

        let newSettings: ITheme = {
            id: themeId === null? "": themeId,
            name: name,
            fontSize: fontSize,
            customPath: "",
            fontFamily: fontFamily, 
            headerColour: headerColour,
            headerFontColour: headerFontColour,
            tableBackgroundColour: tableBackgroundColour, 
            tableBorder: tableBorder,
            tableFontColour: tableFontColour
        }
        try {
            let response: AxiosResponse = await postRequest(auth, `/Theme/SaveTheme`, newSettings);

            toast.success("Theme successfully saved");
        } catch (error) {
            console.log(error);
            toast.error(`Unable to save theme. See log for more info`);
        }
        setSavedDisabled(false);
    }

    const onHeaderFontColourClick = () => {
        setHeaderFontDislay(!headerFontDisplay);
        setHeaderFontColour(headerFontColour);
    }

    const onTableFontColourClick = () => {
        setPrimaryFontDisplay(!primaryFontDisplay);
        setTableFontColour(tableFontColour);
    }
  
    const onHeaderColourClick = () => {
        setHeaderTableDisplay(!headerTableDisplay);
        setHeaderColour(headerColour);
    }

    const onBackgroundColourClick = () => {
        setBackgroundDisplay(!backgroundDisplay);
        setTableBackgroundColour(tableBackgroundColour);
    }

    if (loading) {
        return (
            <Dimmer inverted active={true}>
                <Loader inverted>Loading Theme...</Loader>
            </Dimmer>
        );
    }
    else if (!hasAdminPrivileges) {
        return (
            <ErrorPage title="Access Denied" message=" Only Administrators and Records Managers can view this page" />
        )
    }
    else {
        return (
            <div className="ui container" id="theme-settings">     
                <div className="header blue headerStyleBlue" style={{ marginTop: 20 }}>Configure Table Theme</div>
                <Segment>
                    <div className="header gray headerStyleGray">THEME NAME</div>
                    <div className="ui input fluid" style={{ marginLeft: '10px' }} >
                        <input type="text" style={{ backgroundColor: "#e5e5e5" }} placeholder="Enter Theme Name" id="theme-name" value={name} onChange={(e) => { setName(e.target.value) }} />
                    </div>
                    <div className="header blue headerStyleBlue" style={{marginBottom:10, marginTop:10}}>Border</div>
                    <div className="header gray headerStyleGray">TABLE BORDER DESIGN</div>
                    <div style={{marginLeft:10}}>
                        <Dropdown id="table-border" style={{ backgroundColor: "#e5e5e5" }} options={borderOptions} placeholder='Select Border Design' selection fluid value={tableBorder} onChange={onTableBorderChange} />
                    </div>
                    <Grid style={{marginLeft:10}}>
                        <Grid.Row>
                            <Grid.Column width={8}>
                                <div className="header gray headerStyleGray">FONT</div>
                                <Dropdown id="font-drpdwn" style={{ backgroundColor: "#e5e5e5" , marignLeft:10}} options={fontOptions} placeholder='Select Font' selection fluid value={fontFamily} onChange={onFontChange} />
                            </Grid.Column>
                            <Grid.Column width={8}>
                                <div className="header gray headerStyleGray">FONT SIZE</div>
                                <div className="ui input fluid" style={{ marginLeft: '10px' }} >
                                    <input type="number" style={{ backgroundColor: "#e5e5e5" }} placeholder="Enter Font Size" id="fontsize" onKeyDown={handleKeypress} onBlur={handleBlur} value={fontSize} min={1} max={500} onChange={(e) => { setFontSize(e.target.value) }} />
                                </div>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                    <div className="header blue headerStyleBlue" style={{ marginBottom: 10, marginTop: 10 }}>Colours</div>
                    <Grid style={{ marginLeft: 10 }}>
                        <Grid.Row>
                            <Grid.Column width={8}>
                                <div className="header gray headerStyleGray">TABLE HEADER FONT</div>
                                <div className="ui input">
                                    <input type="text" placeholder={headerFontColour} style={{ backgroundColor: headerFontColour }} />
                                    <Button id="hdr-font-col" className='buttton' onClick={onHeaderFontColourClick} style={{ cursor: 'pointer', marginLeft: '10px' }}>Pick Colour</Button>
                                </div>
                                {headerFontDisplay && (
                                    <div id="colour-picker">
                                        <ChromePicker color={headerFontColour} disableAlpha={true} onChange={(color, event) => { setHeaderFontColour(color.hex) }} />
                                    </div>
                                )}
                            </Grid.Column>
                            <Grid.Column width={8}>
                                <div className="header gray headerStyleGray">TABLE PRIMARY FONT</div>
                                <div className="ui input">
                                    <input type="text" placeholder={tableFontColour} style={{ backgroundColor: tableFontColour }} />
                                    <Button id="primary-font-col" className='buttton' onClick={onTableFontColourClick} style={{ cursor: 'pointer', marginLeft: '10px' }}>Pick Colour</Button>
                                </div>
                                {primaryFontDisplay && (
                                    <div id="colour-picker">
                                        <ChromePicker color={tableFontColour} disableAlpha={true} onChange={(color, event) => { setTableFontColour(color.hex) }} />
                                    </div>
                                )}
                            </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                            <Grid.Column width={8}>
                                <div className="header gray headerStyleGray">TABLE HEADER</div>
                                <div className="ui input">
                                    <input type="text" placeholder={headerColour} style={{ backgroundColor: headerColour }} />
                                    <Button id="table-hdr-font-col" className='buttton' onClick={onHeaderColourClick} style={{ cursor: 'pointer', marginLeft: '10px' }}>Pick Colour</Button>
                                </div>
                                {headerTableDisplay && (
                                    <div id="colour-picker">
                                        <ChromePicker color={headerColour} disableAlpha={true} onChange={(color, event) => { setHeaderColour(color.hex) }} />
                                    </div>
                                )}
                            </Grid.Column>
                            <Grid.Column width={8}>
                                <div className="header gray headerStyleGray">TABLE BACKGROUND</div>
                                <div className="ui input">
                                    <input type="text" placeholder={tableBackgroundColour} style={{ backgroundColor: tableBackgroundColour }} />
                                    <Button id="table-bckgrnd-col" className='buttton' onClick={onBackgroundColourClick} style={{ cursor: 'pointer', marginLeft: '10px' }}>Pick Colour</Button>
                                </div>
                                {backgroundDisplay && (
                                    <div id="colour-picker">
                                        <ChromePicker color={tableBackgroundColour} disableAlpha={true} onChange={(color, event) => { setTableBackgroundColour(color.hex) }} />
                                    </div>
                                )}
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "left", alignContent: "stretch", margin: 10, marginLeft: "20px" }}>
                        <Button id="theme-save" style={{ marginTop: 20 }} onClick={saveTheme} disabled={saveDisabled}>Save</Button>
                        <Button id="theme-cancel" style={{ marginTop: 20, marginLeft: 5 }} onClick={getThemeSettings}>Cancel</Button>
                    </div>
                </Segment>
            </div>
        );
    } 
}

export default ThemeConfig;